import { LayoutDashboard, Target, Briefcase, Contact as ContactsIcon, ClipboardList, FolderKanban, CalendarDays, Users, CalendarRange, CalendarClock, Settings, BarChart3, Activity, Megaphone, Sparkles, Radar, Headphones, MonitorPlay, FileText, NotebookPen, type LucideIcon } from 'lucide-react';
import type { Role } from '@/shared/permissions/roles';
import type { Permission } from '@/shared/permissions/permissions';

export const ROUTES = {
  root: '/',
  login: '/login',
  dashboard: '/dashboard',
  leads: '/leads',
  deals: '/deals',
  contacts: '/contacts',
  tasks: '/tasks',
  projects: '/projects',
  meetings: '/meetings',
  hr: '/hr',
  leaves: '/leaves',
  attendance: '/attendance',
  settings: '/settings',
  reports: '/reports',
  activity: '/activity',
  announcements: '/announcements',
  ai: '/ai',
  scraper: '/scraper',
  callCenter: '/call-center',
  demos: '/demos',
  salesDocs: '/sales-docs',
  notes: '/notes',
} as const;

export interface NavItem {
  label: string;
  path: string;
  icon: LucideIcon;
  /** Optional gating — UI only. */
  permission?: Permission;
  roles?: Role[];
}

export interface NavGroup {
  /** Section label shown above the group; omit for the top-level group. */
  label?: string;
  items: NavItem[];
}

/**
 * Sidebar navigation. Foundation phase ships ONLY Dashboard; each migrated
 * module (Step 4+) appends its entry here. Do not add CRM modules yet.
 */
export const NAV_ITEMS: NavItem[] = [
  { label: 'Dashboard', path: ROUTES.dashboard, icon: LayoutDashboard },
  { label: 'Leads', path: ROUTES.leads, icon: Target },
  { label: 'Lead Generation', path: ROUTES.scraper, icon: Radar },
  { label: 'Call Center', path: ROUTES.callCenter, icon: Headphones },
  { label: 'Deals', path: ROUTES.deals, icon: Briefcase, roles: ['superadmin', 'admin', 'employee', 'bde'] },
  { label: 'Contacts', path: ROUTES.contacts, icon: ContactsIcon },
  { label: 'Tasks', path: ROUTES.tasks, icon: ClipboardList },
  { label: 'Projects', path: ROUTES.projects, icon: FolderKanban },
  { label: 'Project Demos', path: ROUTES.demos, icon: MonitorPlay },
  { label: 'Sales Documents', path: ROUTES.salesDocs, icon: FileText },
  { label: 'Notepad', path: ROUTES.notes, icon: NotebookPen },
  { label: 'Meetings', path: ROUTES.meetings, icon: CalendarDays },
  { label: 'Announcements', path: ROUTES.announcements, icon: Megaphone },
  { label: 'Team & Access', path: ROUTES.hr, icon: Users, roles: ['admin', 'superadmin'] },
  { label: 'Leaves', path: ROUTES.leaves, icon: CalendarRange },
  { label: 'Attendance', path: ROUTES.attendance, icon: CalendarClock },
  { label: 'Activity', path: ROUTES.activity, icon: Activity },
  { label: 'AI Workspace', path: ROUTES.ai, icon: Sparkles },
  { label: 'Reports', path: ROUTES.reports, icon: BarChart3, roles: ['admin', 'superadmin'] },
  { label: 'Settings', path: ROUTES.settings, icon: Settings },
];

/**
 * Grouped navigation for the enterprise sidebar. Logical sections keep the
 * ~20 modules scannable. Role gating is identical to NAV_ITEMS (UI-only;
 * the server stays authoritative). NAV_ITEMS is retained for compatibility.
 */
export const NAV_GROUPS: NavGroup[] = [
  {
    items: [{ label: 'Dashboard', path: ROUTES.dashboard, icon: LayoutDashboard }],
  },
  {
    label: 'Sales',
    items: [
      { label: 'Leads', path: ROUTES.leads, icon: Target },
      { label: 'Deals', path: ROUTES.deals, icon: Briefcase, roles: ['superadmin', 'admin', 'employee', 'bde'] },
      { label: 'Contacts', path: ROUTES.contacts, icon: ContactsIcon },
      { label: 'Sales Documents', path: ROUTES.salesDocs, icon: FileText },
    ],
  },
  {
    label: 'Work',
    items: [
      { label: 'Tasks', path: ROUTES.tasks, icon: ClipboardList },
      { label: 'Projects', path: ROUTES.projects, icon: FolderKanban },
      { label: 'Project Demos', path: ROUTES.demos, icon: MonitorPlay },
      { label: 'Meetings', path: ROUTES.meetings, icon: CalendarDays },
      { label: 'Notepad', path: ROUTES.notes, icon: NotebookPen },
    ],
  },
  {
    label: 'Communication',
    items: [
      { label: 'Announcements', path: ROUTES.announcements, icon: Megaphone },
      { label: 'Activity', path: ROUTES.activity, icon: Activity },
      { label: 'AI Workspace', path: ROUTES.ai, icon: Sparkles },
    ],
  },
  {
    label: 'Operations',
    items: [
      { label: 'Lead Generation', path: ROUTES.scraper, icon: Radar },
      { label: 'Call Center', path: ROUTES.callCenter, icon: Headphones },
    ],
  },
  {
    label: 'People',
    items: [
      { label: 'Attendance', path: ROUTES.attendance, icon: CalendarClock },
      { label: 'Leaves', path: ROUTES.leaves, icon: CalendarRange },
      { label: 'Team & Access', path: ROUTES.hr, icon: Users, roles: ['admin', 'superadmin'] },
    ],
  },
  {
    label: 'Insights',
    items: [{ label: 'Reports', path: ROUTES.reports, icon: BarChart3, roles: ['admin', 'superadmin'] }],
  },
  {
    label: 'System',
    items: [{ label: 'Settings', path: ROUTES.settings, icon: Settings }],
  },
];
